import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { usePlatform } from '../context/PlatformContext';
import Modal from '../components/ui/Modal';
import StatusBadge from '../components/ui/StatusBadge';
import {
  FiAward, FiRotateCcw, FiArchive, FiAlertTriangle, FiRefreshCw, FiSearch,
  FiFilter, FiChevronDown, FiExternalLink, FiClock, FiShield, FiUser,
  FiArrowRightCircle, FiXCircle, FiPlus, FiCopy, FiCheckCircle,
  FiFileText, FiLayers, FiUpload, FiCheck, FiMail, FiDownload
} from 'react-icons/fi';
import { downloadCertificateWithTemplate, openCertificatePDF } from '../utils/pdfGenerator';

// ─── helpers ──────────────────────────────────────────────────────────────────
function isExpired(c)     { return c.expiryDate && new Date(c.expiryDate) < new Date(); }
function isExpiringSoon(c){
  if (!c.expiryDate) return false;
  const diff = (new Date(c.expiryDate) - new Date()) / 86400000;
  return diff > 0 && diff <= 30;
}

// ─── main component ────────────────────────────────────────────────────────────
export default function Certificates() {
  const {
    mode, candidates, updateCandidate,
    revokeCredential, renewCredential, reissueCredential, archiveCredential,
    transferOwnership, settings,
    internshipCandidates,          // offer-letter recipients from internship slice
    allData,                       // full data tree for cross-mode checks
    issueCertificateFromOfferLetter,
  } = usePlatform();

  // ── tab state ───────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('issue');

  // ── "Issue" tab state ───────────────────────────────────────────────────────
  const [issueSearch, setIssueSearch]     = useState('');
  const [selectedIds, setSelectedIds]     = useState(new Set());
  const [generating,  setGenerating]      = useState(false);

  // ── "Manage" tab state ──────────────────────────────────────────────────────
  const [search,        setSearch]        = useState('');
  const [filterType,    setFilterType]    = useState('all');
  const [revokeModal,   setRevokeModal]   = useState(null);
  const [renewModal,    setRenewModal]    = useState(null);
  const [reissueModal,  setReissueModal]  = useState(null);
  const [transferModal, setTransferModal] = useState(null);
  const [historyModal,  setHistoryModal]  = useState(null);
  const [revokeReason,  setRevokeReason]  = useState('');
  const [renewDays,     setRenewDays]     = useState(365);
  const [transferEmail, setTransferEmail] = useState('');

  // ── "Issue" tab: candidates eligible for certificates ───────────────────────
  // Offer-letter recipients = internship candidates whose doc was generated
  const offerLetterRecipients = useMemo(() =>
    internshipCandidates.filter(c => c.docStatus === 'generated' || c.generatedId || c.verificationId),
    [internshipCandidates]
  );

  // Build a lookup: sourceOfferLetterId → certificate record
  const issuedCertMap = useMemo(() => {
    const map = {};
    (allData.certificate?.candidates || []).forEach(cert => {
      if (cert.sourceOfferLetterId) map[cert.sourceOfferLetterId] = cert;
    });
    return map;
  }, [allData]);

  const getCertForIntern = (intern) =>
    issuedCertMap[intern.generatedId] || issuedCertMap[intern.verificationId];

  const filteredOfferRecipients = useMemo(() => {
    const q = issueSearch.toLowerCase();
    return offerLetterRecipients.filter(c =>
      !q ||
      c.name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.generatedId?.toLowerCase().includes(q)
    );
  }, [offerLetterRecipients, issueSearch]);

  // ── "Manage" tab: already-generated certificates ────────────────────────────
  const allCerts = useMemo(
    () => candidates.filter(c => c.docStatus === 'generated' || c.generatedId || c.verificationId),
    [candidates]
  );

  const filteredCerts = useMemo(() => {
    const q = search.toLowerCase();
    return allCerts.filter(c => {
      const matchSearch = !q || c.name?.toLowerCase().includes(q) || c.generatedId?.toLowerCase().includes(q);
      const matchFilter = filterType === 'all' || c.certStatus === filterType;
      return matchSearch && matchFilter;
    });
  }, [allCerts, search, filterType]);

  // ── selection helpers ────────────────────────────────────────────────────────
  const toggleSelect = (id) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () => {
    const eligible = filteredOfferRecipients.filter(c => !getCertForIntern(c));
    if (selectedIds.size === eligible.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(eligible.map(c => c.id)));
    }
  };

  // ── generate certificates ────────────────────────────────────────────────────
  const handleGenerateSingle = async (intern) => {
    setGenerating(true);
    try {
      const certVerifId = issueCertificateFromOfferLetter(intern);
      toast.success(
        <div>
          <div className="font-bold">Certificate Issued ✓</div>
          <div className="text-xs font-mono mt-0.5 opacity-75">{certVerifId}</div>
        </div>
      );
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate certificate. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateBulk = async () => {
    if (selectedIds.size === 0) return;
    setGenerating(true);
    const toGenerate = filteredOfferRecipients.filter(
      c => selectedIds.has(c.id) && !getCertForIntern(c)
    );
    try {
      for (const intern of toGenerate) {
        issueCertificateFromOfferLetter(intern);
        // small delay between bulk operations to allow state to settle
        await new Promise(r => setTimeout(r, 60));
      }
      toast.success(`${toGenerate.length} certificate${toGenerate.length > 1 ? 's' : ''} issued successfully!`);
      setSelectedIds(new Set());
    } catch (err) {
      console.error(err);
      toast.error('Some certificates may not have been generated. Please check the registry.');
    } finally {
      setGenerating(false);
    }
  };

  // ── lifecycle handlers ───────────────────────────────────────────────────────
  const handleRevoke = () => {
    if (!revokeReason.trim()) return toast.error('Please enter a reason.');
    revokeCredential(revokeModal.id, revokeReason);
    toast.success(`Certificate ${revokeModal.generatedId} revoked.`);
    setRevokeModal(null); setRevokeReason('');
  };

  const handleRenew = () => {
    renewCredential(renewModal.id, renewDays);
    toast.success(`Certificate renewed for ${renewDays} more days!`);
    setRenewModal(null);
  };

  const handleReissue = () => {
    reissueCredential(reissueModal.id);
    toast.success('Certificate reissued with new verification ID!');
    setReissueModal(null);
  };

  const handleTransfer = () => {
    if (!transferEmail.trim()) return toast.error('Enter new owner email.');
    transferOwnership(transferModal.id, transferEmail);
    toast.success(`Ownership transferred to ${transferEmail}`);
    setTransferModal(null); setTransferEmail('');
  };

  const statusOptions = ['all', 'active', 'revoked', 'archived', 'reissued'];
  const eligibleCount = offerLetterRecipients.filter(c => !getCertForIntern(c)).length;
  const issuedCount   = offerLetterRecipients.filter(c => !!getCertForIntern(c)).length;

  return (
    <div className="space-y-6">
      {/* ── Page Header ───────────────────────────────────────────── */}
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <h1 className="page-title">Certificate Registry</h1>
          <p className="page-subtitle">
            Issue completion certificates to existing Offer Letter recipients · Manage credential lifecycle
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <span className="px-4 py-2.5 bg-emerald-50 border border-emerald-100 rounded-xl text-xs font-bold text-emerald-700">
            ✓ Inbuilt Lifetime Validity
          </span>
          <span className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600">
            {allCerts.length} Certificates Issued
          </span>
        </div>
      </div>

      {/* ── Stats ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Offer Letter Recipients', val: offerLetterRecipients.length, color: 'text-sky-600',     bg: 'bg-sky-50'     },
          { label: 'Certs Pending',           val: eligibleCount,                color: 'text-amber-600',   bg: 'bg-amber-50'   },
          { label: 'Certs Issued',            val: issuedCount,                  color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Total Managed',           val: allCerts.length,             color: 'text-indigo-600',  bg: 'bg-indigo-50'  },
        ].map(s => (
          <div key={s.label} className={`glass-card rounded-2xl px-5 py-4 ${s.bg}`}>
            <div className="section-title mb-1">{s.label}</div>
            <div className={`text-3xl font-black ${s.color}`}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* ── Tab Bar ───────────────────────────────────────────────── */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-2xl w-fit">
        {[
          { id: 'issue',  label: 'Issue Certificates', icon: FiAward       },
          { id: 'manage', label: 'Manage Registry',    icon: FiLayers      },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${
              activeTab === tab.id
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.id === 'issue' && eligibleCount > 0 && (
              <span className="ml-1 w-5 h-5 rounded-full bg-amber-400 text-white text-[10px] font-black flex items-center justify-center">
                {eligibleCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ═══════════════════════════════════════════════════════════
          TAB: ISSUE CERTIFICATES
      ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'issue' && (
        <div className="space-y-5">
          {/* Info banner */}
          <div className="glass-card rounded-2xl p-5 flex items-start gap-4 border-l-4 border-l-sky-400 bg-sky-50/60">
            <FiFileText className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-sky-900 text-sm">Use Existing Offer Letter Recipients</div>
              <p className="text-sky-700 text-xs mt-0.5 leading-relaxed">
                Select candidates who already received an Offer Letter and generate their
                Completion Certificate. A new Credential ID and QR code will be created for
                each certificate — the original Offer Letter will not be modified.
              </p>
            </div>
          </div>

          {/* Toolbar */}
          <div className="glass-card rounded-2xl p-4 flex flex-wrap gap-3 items-center justify-between">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                value={issueSearch}
                onChange={e => setIssueSearch(e.target.value)}
                placeholder="Search by name, email, ID..."
                className="border border-slate-200 rounded-xl pl-10 pr-4 py-2 text-sm w-64 focus:outline-none focus:border-sky-400 transition-all"
              />
            </div>
            {selectedIds.size > 0 && (
              <button
                onClick={handleGenerateBulk}
                disabled={generating}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-sm font-bold transition-all disabled:opacity-50 shadow-md shadow-emerald-500/20"
              >
                {generating ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <FiAward className="w-4 h-4" />
                )}
                Generate {selectedIds.size} Certificate{selectedIds.size > 1 ? 's' : ''}
              </button>
            )}
          </div>

          {/* Table */}
          {offerLetterRecipients.length === 0 ? (
            <div className="glass-card rounded-2xl p-16 text-center space-y-3">
              <FiFileText className="w-12 h-12 text-slate-200 mx-auto" />
              <p className="text-slate-700 font-bold">No Offer Letter recipients found.</p>
              <p className="text-slate-400 text-sm">
                Generate and issue Offer Letters from the Candidates page first, then return here to issue Completion Certificates.
              </p>
            </div>
          ) : filteredOfferRecipients.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center">
              <p className="text-slate-500 font-semibold">No candidates match your search.</p>
            </div>
          ) : (
            <div className="glass-card rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="px-4 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        className="rounded"
                        onChange={toggleAll}
                        checked={
                          selectedIds.size > 0 &&
                          selectedIds.size === filteredOfferRecipients.filter(c => !getCertForIntern(c)).length
                        }
                      />
                    </th>
                    <th className="px-4 py-3 text-left font-bold text-slate-600 text-xs uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left font-bold text-slate-600 text-xs uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left font-bold text-slate-600 text-xs uppercase tracking-wider">Intern ID</th>
                    <th className="px-4 py-3 text-left font-bold text-slate-600 text-xs uppercase tracking-wider">Offer Letter</th>
                    <th className="px-4 py-3 text-left font-bold text-slate-600 text-xs uppercase tracking-wider">Certificate</th>
                    <th className="px-4 py-3 text-center font-bold text-slate-600 text-xs uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredOfferRecipients.map(intern => {
                    const existingCert = getCertForIntern(intern);
                    const isSelected  = selectedIds.has(intern.id);
                    return (
                      <motion.tr
                        key={intern.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={`hover:bg-slate-50 transition-colors ${isSelected ? 'bg-sky-50/50' : ''}`}
                      >
                        <td className="px-4 py-3">
                          {!existingCert ? (
                            <input
                              type="checkbox"
                              className="rounded"
                              checked={isSelected}
                              onChange={() => toggleSelect(intern.id)}
                            />
                          ) : (
                            <FiCheck className="w-4 h-4 text-emerald-500 mx-auto" />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-900">{intern.name}</div>
                          <div className="text-xs text-slate-400">{intern.role || intern.domain || 'Intern'}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-xs">{intern.email}</td>
                        <td className="px-4 py-3">
                          <code className="text-xs bg-sky-50 text-sky-700 px-2 py-1 rounded-lg font-mono">
                            {intern.generatedId || '—'}
                          </code>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-lg text-[10px] font-black uppercase">
                            <FiCheckCircle className="w-3 h-3" /> Issued
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {existingCert ? (
                            <div>
                              <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-violet-50 text-violet-700 border border-violet-100 rounded-lg text-[10px] font-black uppercase">
                                <FiAward className="w-3 h-3" /> Generated
                              </span>
                              <code className="block text-[10px] font-mono text-slate-400 mt-1">
                                {existingCert.generatedId}
                              </code>
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-amber-50 text-amber-600 border border-amber-100 rounded-lg text-[10px] font-black uppercase">
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {existingCert ? (
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `${window.location.origin}/#/verify/${existingCert.verificationId}`
                                );
                                toast.success('Certificate verify link copied!');
                              }}
                              className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-[10px] font-bold hover:bg-slate-200 transition-all mx-auto"
                            >
                              <FiCopy className="w-3 h-3" /> Verify Link
                            </button>
                          ) : (
                            <button
                              onClick={() => handleGenerateSingle(intern)}
                              disabled={generating}
                              className="flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white rounded-xl text-[11px] font-bold transition-all disabled:opacity-50 shadow-md shadow-violet-500/20 mx-auto"
                            >
                              {generating ? (
                                <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                              ) : (
                                <FiAward className="w-3 h-3" />
                              )}
                              Generate
                            </button>
                          )}
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          TAB: MANAGE CERTIFICATES (existing lifecycle)
      ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'manage' && (
        <div className="space-y-5">
          {/* Filters */}
          <div className="glass-card rounded-2xl p-4 flex flex-wrap gap-3 items-center">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search certificates..."
                className="border border-slate-200 rounded-xl pl-10 pr-4 py-2 text-sm w-56 focus:outline-none focus:border-sky-400 transition-all"
              />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {statusOptions.map(s => (
                <button
                  key={s}
                  onClick={() => setFilterType(s)}
                  className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                    filterType === s ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Certificate Cards */}
          {filteredCerts.length === 0 ? (
            <div className="glass-card rounded-2xl p-16 text-center space-y-3">
              <FiAward className="w-12 h-12 text-slate-200 mx-auto" />
              <p className="text-slate-700 font-bold">No certificates found.</p>
              <p className="text-slate-400 text-sm">
                Switch to the <strong>Issue Certificates</strong> tab to generate certificates
                for existing Offer Letter recipients.
              </p>
              <button
                onClick={() => setActiveTab('issue')}
                className="mt-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-500 text-white rounded-xl text-sm font-bold transition-all"
              >
                Go to Issue Certificates
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredCerts.map(cert => {
                const expired = isExpired(cert);
                const expSoon = isExpiringSoon(cert);
                return (
                  <motion.div
                    key={cert.id}
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="glass-card rounded-2xl p-6 group relative overflow-hidden"
                  >
                    <div className={`absolute top-0 left-0 w-full h-1 ${
                      cert.certStatus === 'revoked' ? 'bg-rose-500'
                      : expired ? 'bg-orange-400'
                      : expSoon  ? 'bg-amber-400'
                      : 'bg-emerald-500'
                    }`} />
                    <div className="flex justify-between items-start mb-4 mt-1">
                      <div className="w-12 h-12 rounded-2xl bg-sky-50 flex items-center justify-center">
                        <FiAward className="text-sky-500 w-6 h-6" />
                      </div>
                      <StatusBadge status={
                        cert.certStatus === 'revoked' ? 'revoked'
                        : expired ? 'expired'
                        : cert.certStatus || 'active'
                      } />
                    </div>
                    <h3 className="font-semibold text-navy-900 text-base mb-1 truncate">{cert.name}</h3>
                    <div className="font-mono text-xs text-sky-600 bg-sky-50 px-2 py-1 rounded-lg inline-block mb-1">
                      {cert.generatedId}
                    </div>
                    {/* Source offer letter link */}
                    {cert.sourceOfferLetterId && (
                      <div className="text-[10px] text-slate-400 font-mono mb-2">
                        From OL: {cert.sourceOfferLetterId}
                      </div>
                    )}
                    <div className="space-y-1 text-xs text-slate-800 font-semibold mb-4">
                      <div>Email: {cert.email}</div>
                      {cert.expiryDate && (
                        <div className={
                          expSoon && cert.certStatus !== 'revoked' ? 'text-amber-600 font-bold'
                          : expired ? 'text-rose-600 font-bold' : ''
                        }>
                          Expires: {new Date(cert.expiryDate).toLocaleDateString()}
                          {expSoon && cert.certStatus !== 'revoked' ? ' ⚠️ Soon' : expired ? ' 🔴 Expired' : ''}
                        </div>
                      )}
                      {cert.verificationId && (
                        <div className="font-mono">Verify ID: {cert.verificationId}</div>
                      )}
                      {cert.certStatus === 'revoked' && (
                        <div className="text-rose-600 font-bold">Reason: {cert.revokeReason}</div>
                      )}
                    </div>
                    {cert.versionHistory?.length > 0 && (
                      <button
                        onClick={() => setHistoryModal(cert)}
                        className="text-[10px] text-slate-700 font-bold hover:text-sky-600 uppercase tracking-widest mb-3 flex items-center gap-1"
                      >
                        <FiClock className="w-3 h-3" /> {cert.versionHistory.length} version event(s)
                      </button>
                    )}
                    {/* Actions */}
                    <div className="flex flex-wrap gap-1.5">
                      {cert.certStatus !== 'revoked' && cert.certStatus !== 'archived' && (
                        <>
                          <button onClick={() => setRenewModal(cert)} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-lg text-[10px] font-bold hover:bg-emerald-100 transition-all">
                            <FiRefreshCw className="w-3 h-3" /> Renew
                          </button>
                          <button onClick={() => setReissueModal(cert)} className="flex items-center gap-1 px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-lg text-[10px] font-bold hover:bg-indigo-100 transition-all">
                            <FiRotateCcw className="w-3 h-3" /> Reissue
                          </button>
                          <button onClick={() => setTransferModal(cert)} className="flex items-center gap-1 px-3 py-1.5 bg-violet-50 text-violet-700 border border-violet-100 rounded-lg text-[10px] font-bold hover:bg-violet-100 transition-all">
                            <FiArrowRightCircle className="w-3 h-3" /> Transfer
                          </button>
                          <button onClick={() => setRevokeModal(cert)} className="flex items-center gap-1 px-3 py-1.5 bg-rose-50 text-rose-700 border border-rose-100 rounded-lg text-[10px] font-bold hover:bg-rose-100 transition-all">
                            <FiXCircle className="w-3 h-3" /> Revoke
                          </button>
                        </>
                      )}
                      {cert.certStatus !== 'archived' && (
                        <button
                          onClick={() => { archiveCredential(cert.id); toast.success('Archived.'); }}
                          className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-[10px] font-bold hover:bg-slate-200 transition-all"
                        >
                          <FiArchive className="w-3 h-3" /> Archive
                        </button>
                      )}
                      <button
                        onClick={async () => {
                          try {
                            toast.loading('Generating PDF...', { id: `pdf-${cert.id}` });
                            await downloadCertificateWithTemplate(cert, '/Certificate.jpeg', settings?.certificate, 'certificate');
                            toast.success('Certificate PDF downloaded!', { id: `pdf-${cert.id}` });
                          } catch (e) {
                            toast.error('Download failed', { id: `pdf-${cert.id}` });
                          }
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-800 border border-amber-200 rounded-lg text-[10px] font-bold hover:bg-amber-100 transition-all"
                      >
                        <FiDownload className="w-3 h-3 text-amber-600" /> PDF
                      </button>
                      <button
                        onClick={() => {
                          openCertificatePDF(cert, '/Certificate.jpeg', settings?.certificate, 'certificate');
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-[10px] font-bold hover:bg-slate-200 transition-all"
                      >
                        <FiExternalLink className="w-3 h-3" /> Preview
                      </button>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/#/verify/${cert.verificationId}`);
                          toast.success('Verify link copied!');
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-[10px] font-bold hover:bg-slate-200 transition-all"
                      >
                        <FiCopy className="w-3 h-3" /> Verify Link
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Modals (unchanged from original) ─────────────────────── */}
      <Modal isOpen={!!revokeModal} onClose={() => setRevokeModal(null)} title="Revoke Certificate">
        {revokeModal && (
          <div className="p-6 space-y-4">
            <div className="p-4 bg-rose-50 border border-rose-100 rounded-2xl text-sm text-rose-800">
              ⚠️ Revoking <strong>{revokeModal.generatedId}</strong> for <strong>{revokeModal.name}</strong>. This action cannot be undone.
            </div>
            <div>
              <label className="block section-title mb-2">Reason for Revocation *</label>
              <textarea
                value={revokeReason}
                onChange={e => setRevokeReason(e.target.value)}
                rows={3}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 resize-none"
                placeholder="E.g., Academic misconduct, Duplicate issuance..."
              />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setRevokeModal(null)} className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm">Cancel</button>
              <button onClick={handleRevoke} className="px-8 py-2.5 bg-rose-600 text-white rounded-xl font-bold text-sm hover:bg-rose-500 transition-all">Revoke Certificate</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={!!renewModal} onClose={() => setRenewModal(null)} title="Renew Certificate">
        {renewModal && (
          <div className="p-6 space-y-4">
            <p className="text-slate-600">Renewing certificate for <strong>{renewModal.name}</strong> ({renewModal.generatedId})</p>
            <div>
              <label className="block section-title mb-2">Extend by (Days)</label>
              <input type="number" value={renewDays} onChange={e => setRenewDays(parseInt(e.target.value))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-400" />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setRenewModal(null)} className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm">Cancel</button>
              <button onClick={handleRenew} className="px-8 py-2.5 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-500 transition-all">Renew Certificate</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={!!reissueModal} onClose={() => setReissueModal(null)} title="Reissue Certificate">
        {reissueModal && (
          <div className="p-6 space-y-4">
            <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-2xl text-sm text-indigo-800">
              Reissuing will generate a new Verification ID for <strong>{reissueModal.name}</strong> ({reissueModal.generatedId}). The old ID will be invalidated.
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setReissueModal(null)} className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm">Cancel</button>
              <button onClick={handleReissue} className="px-8 py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-500 transition-all">Reissue Certificate</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={!!transferModal} onClose={() => setTransferModal(null)} title="Transfer Ownership">
        {transferModal && (
          <div className="p-6 space-y-4">
            <p className="text-slate-600">Transfer ownership of <strong>{transferModal.generatedId}</strong></p>
            <div>
              <label className="block section-title mb-2">New Owner Email</label>
              <input type="email" value={transferEmail} onChange={e => setTransferEmail(e.target.value)} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-400" placeholder="newowner@example.com" />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setTransferModal(null)} className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm">Cancel</button>
              <button onClick={handleTransfer} className="px-8 py-2.5 bg-violet-600 text-white rounded-xl font-bold text-sm hover:bg-violet-500 transition-all">Transfer</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={!!historyModal} onClose={() => setHistoryModal(null)} title={`Version History — ${historyModal?.generatedId}`}>
        {historyModal && (
          <div className="p-6">
            <div className="space-y-3">
              {historyModal.versionHistory.map((e, i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="w-8 h-8 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-xs font-black">
                    v{historyModal.versionHistory.length - i}
                  </div>
                  <div>
                    <div className="font-bold text-slate-800 capitalize">{e.action}</div>
                    <div className="text-xs text-slate-800 font-semibold">{new Date(e.date).toLocaleString()}</div>
                    {e.oldVerifId && <div className="text-xs font-mono text-slate-300">Old ID: {e.oldVerifId}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
